import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import type { LinhaCrianca } from './types';

export type EstadoRelatorio = 'carregando' | 'pronto' | 'erro';

interface Retorno {
  estado: EstadoRelatorio;
  linhas: LinhaCrianca[];
  eventoNome: string;
  erro: string | null;
  recarregar: () => void;
}

// Lê a lista de crianças do evento (só a direção consegue).
export function useRelatorioCriancas(eventoId: number): Retorno {
  const [estado, setEstado] = useState<EstadoRelatorio>('carregando');
  const [linhas, setLinhas] = useState<LinhaCrianca[]>([]);
  const [eventoNome, setEventoNome] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ativo = true;

    if (!eventoId || Number.isNaN(eventoId)) {
      setEstado('erro');
      setErro('Evento não encontrado.');
      return () => {
        ativo = false;
      };
    }

    setEstado('carregando');
    setErro(null);

    (async () => {
      try {
        const [res, ev] = await Promise.all([
          supabase.rpc('relatorio_criancas', { p_evento: eventoId }),
          supabase.from('eventos').select('nome').eq('id', eventoId).maybeSingle(),
        ]);
        if (res.error) throw res.error;
        if (!ativo) return;

        const lista = Array.isArray(res.data) ? (res.data as LinhaCrianca[]) : [];
        const nome = (ev.data as { nome?: string } | null)?.nome ?? '';
        setLinhas(lista);
        setEventoNome(nome);
        setEstado('pronto');
      } catch (e) {
        if (!ativo) return;
        setErro(mensagemAmigavel(e, 'Não foi possível abrir a lista de crianças.'));
        setEstado('erro');
      }
    })();

    return () => {
      ativo = false;
    };
  }, [eventoId, reload]);

  return { estado, linhas, eventoNome, erro, recarregar: () => setReload((r) => r + 1) };
}